import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ReviewBrowserRenderProgress,
  ReviewClientRenderedVideoRequest,
  ReviewClientRenderReserveRequest
} from '../../../Review/models/review-browser-render.model';
import { ReviewService } from '../../../Review/services/review.service';
import { PipelineLoaderService } from '../../../../shared/components/pipeline-loader/pipeline-loader.service';
import { JivuCommandProgressReporter } from '../../../JivuCommand/services/jivu-command-progress-reporter.service';
import { JivuCommandPollerService } from '../../../JivuCommand/services/jivu-command-poller.service';
import { JivuCommandService } from '../../../JivuCommand/services/jivu-command.service';
import {
  SmartVideoAssetsRenderFlowService
} from '../smart-video/smart-video-assets-render-flow.service';
import {
  SmartVideoAssetSequenceRenderOptions
} from '../smart-video/smart-video-asset-sequence-renderer.service';

/**
 * Render options the page hands over, minus `onProgress` — the service installs its own progress
 * forwarder (page → command). Everything else (title/description/assets/aspectRatio/subtitles/
 * outputSettings) is still built on the page from the SAME expressions used today.
 */
export type AssetSequenceCommandRenderOptions = Omit<SmartVideoAssetSequenceRenderOptions, 'onProgress'>;

/**
 * The save fields the PAGE knows up front (title/description + runtime-narration voice + captions).
 * The render/upload-derived fields (videoFileId/Name/ContentType, thumbnail*, durationSeconds,
 * clientRuntime) are filled in by the service after reserve + upload, so they are omitted here.
 */
export type AssetSequenceCommandSavePayloadBase = Omit<
  ReviewClientRenderedVideoRequest,
  | 'videoFileId'
  | 'videoFileName'
  | 'videoContentType'
  | 'thumbnailFileId'
  | 'thumbnailFileName'
  | 'thumbnailContentType'
  | 'durationSeconds'
  | 'clientRuntime'
>;

/** Everything the page hands over to drive an `asset_sequence_render` command end-to-end. */
export interface AssetSequenceCommandRenderInput {
  /** Built on the page from the SAME expressions as the inline path (response + asset/voice prep). */
  renderOptions: AssetSequenceCommandRenderOptions;
  /** Passed straight to `reviewService.reserveClientRenderedVideo(...)` — the reserve mints the key. */
  reservePayload: ReviewClientRenderReserveRequest;
  /** Static + page-state save fields; merged with the render/upload-derived fields inside the service. */
  savePayloadBase: AssetSequenceCommandSavePayloadBase;
  /** Result object posted to the command's `complete` endpoint. */
  completeResult: unknown;
  /** Human-readable message for the command completion (page-owned wording). */
  completeMessage: string;
  /**
   * Global pipeline-loader operation id (PipelineLoaderService is root-scoped). The service advances
   * it from inside `drive` so the loader keeps moving even after the Review page is destroyed.
   */
  loaderId?: string;
  /**
   * Best-effort side effect the page runs once the reserve lands (e.g. persisting the runtime voice
   * choice to localStorage keyed by reserve.id). Runs inside the service so the reserve id is known.
   */
  onReserved?: (reserveId: string) => void;
  /**
   * Optional page-local progress sink (e.g. the in-page render overlay signal) for while the Review
   * page is still mounted. No-ops harmlessly once the page is gone; the command + global loader are
   * the survival path.
   */
  onLocalProgress?: (progress: ReviewBrowserRenderProgress) => void;
}

/**
 * Root-level Assets-Use (`asset_sequence_render`) command render driver.
 *
 * Wave 3 of the render refactor: relocates the page-bound ORCHESTRATION (render → R2 reserve →
 * upload → save → complete/fail the Jivu Command) for the Assets-Use flow OUT of
 * `unified-review.page.ts` (which is destroyed on route navigation) and INTO this
 * `providedIn: 'root'` singleton. Because the service lives at the application root, an Assets-Use
 * command keeps rendering, uploading, saving, and completing even after the Review page is gone —
 * the heaviest of the five flows, where the whole chain previously lived in the PAGE.
 *
 * STRICTLY ADDITIVE. The render still happens inside `SmartVideoAssetsRenderFlowService` and the R2
 * reserve/upload/save still happen via the SAME `ReviewService` calls the page makes today (R2
 * layout frozen — the reserve mints the key server-side; keys are never built here). The page still
 * builds the render options (asset list, voice, narration-TTS baking) and makes the BE
 * `generateSmartVideoFromAssets` call; only the render → upload → complete chain delegates here. The
 * non-command (direct generate) page path is untouched; only the command case delegates.
 */
@Injectable({ providedIn: 'root' })
export class AssetSequenceCommandRenderService {
  private readonly assetsRenderer = inject(SmartVideoAssetsRenderFlowService);
  private readonly reviewService = inject(ReviewService);
  private readonly pipelineLoader = inject(PipelineLoaderService);
  private readonly progressReporter = inject(JivuCommandProgressReporter);
  private readonly command = inject(JivuCommandService);
  private readonly poller = inject(JivuCommandPollerService);

  /**
   * Command ids currently being driven — guards against double-drive (e.g. a re-entrant render) AND
   * tracks what is cancellable. `cancel()` is the background-worker handle's `.cancel`, populated once
   * the render starts; `aborted` is checked at each await boundary so a cancel that lands after the
   * render finishes still skips R2 reserve/upload/save + completion.
   */
  private readonly inFlight = new Map<string, { cancel: (() => void) | null; aborted: boolean }>();

  /**
   * Drive an Assets-Use command end-to-end: render the prepared assets, reserve the R2 target,
   * upload the rendered video + thumbnail, save the Review record, then complete (or fail) the
   * command. Reuses the page's exact ReviewService calls. Survives route teardown because this
   * service is root-scoped.
   */
  async drive(commandId: string, input: AssetSequenceCommandRenderInput): Promise<void> {
    const id = (commandId || '').trim();
    if (!id || this.inFlight.has(id)) return;
    const entry = { cancel: null as (() => void) | null, aborted: false };
    this.inFlight.set(id, entry);

    const loaderId = input.loaderId;
    try {
      const rendered = await this.assetsRenderer.renderAssetsToVideo({
        ...input.renderOptions,
        onHandle: cancel => { entry.cancel = cancel; },
        onProgress: progress => {
          // Page-local overlay (while mounted) + global loader (survives teardown).
          input.onLocalProgress?.(progress);
          if (loaderId) {
            this.pipelineLoader.update(loaderId, {
              message: progress.message,
              detail: progress.phase,
              progress: progress.percent
            });
          }
          // C3 — forward throttled render progress to the sender (40 → 95) just as the page did.
          this.progressReporter.report(id, Math.round(40 + progress.percent * 0.55), progress.message);
        }
      });

      // Cancelled mid/after-render: the cancel path already marked the command cancelled — bail
      // WITHOUT reserving R2, uploading, saving, or completing.
      if (entry.aborted) return;

      input.onLocalProgress?.({ phase: 'uploading', percent: 96, message: 'Uploading video...' });
      if (loaderId) {
        this.pipelineLoader.update(loaderId, { message: 'Uploading rendered video...', progress: 96 });
      }
      const reserve = await firstValueFrom(this.reviewService.reserveClientRenderedVideo(input.reservePayload));
      input.onReserved?.(reserve.id);

      const uploads = await firstValueFrom(this.reviewService.uploadRenderedReviewAssetsToFolder(rendered, reserve));

      await firstValueFrom(this.reviewService.saveClientRenderedVideo(reserve.id, {
        ...input.savePayloadBase,
        videoFileId: uploads.video.filePath,
        videoFileName: uploads.video.fileName,
        videoContentType: uploads.video.contentType,
        thumbnailFileId: uploads.thumbnail?.filePath ?? null,
        thumbnailFileName: uploads.thumbnail?.fileName ?? null,
        thumbnailContentType: uploads.thumbnail?.contentType ?? null,
        durationSeconds: rendered.durationSeconds,
        clientRuntime: rendered.clientRuntime ?? 'browser'
      }));

      input.onLocalProgress?.({ phase: 'complete', percent: 100, message: 'Video processing complete.' });
      if (loaderId) {
        this.pipelineLoader.complete(loaderId, { message: 'assets-library video is ready in Review.' });
      }
      this.progressReporter.clear(id);
      await firstValueFrom(this.command.complete(id, input.completeResult, input.completeMessage));
      this.poller.claimNextQueuedCommandSoon(id);
    } catch (err) {
      // A cancel aborts the worker, which rejects the render promise — that is expected teardown,
      // not a failure. The cancel path already marked the command cancelled, so swallow it.
      if (entry.aborted) {
        if (loaderId) this.pipelineLoader.cancel(loaderId, { message: 'assets-library video cancelled.' });
        return;
      }
      if (loaderId) {
        this.pipelineLoader.fail(loaderId, { message: 'assets-library video failed.', detail: this.errorMessage(err) });
      }
      this.progressReporter.clear(id);
      await firstValueFrom(this.command.fail(id, this.errorMessage(err)));
      this.poller.claimNextQueuedCommandSoon(id);
    } finally {
      this.inFlight.delete(id);
    }
  }

  /**
   * Abort the in-flight client render for this command (Wave 5). Sets the abort flag (so a render
   * that is already finishing skips the R2 reserve/upload/save + complete) and stops the background
   * worker via its stored `.cancel`. The existing cancel cascade (BE job + command record) is handled
   * by the caller; this only kills the LOCAL render. No-op if the command is not being driven here.
   */
  cancel(commandId: string): void {
    const id = (commandId || '').trim();
    const entry = this.inFlight.get(id);
    if (!entry) return;
    entry.aborted = true;
    try {
      entry.cancel?.();
    } catch {
      // Best effort — the worker may already be gone.
    }
    this.progressReporter.clear(id);
    this.inFlight.delete(id);
  }

  private errorMessage(err: unknown): string {
    return err instanceof Error ? err.message : 'Assets-Use render failed.';
  }
}
